import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import MouseTrail from '../components/MouseTrail';
import TextScramble from '../components/TextScramble';
import { Send, Mail, MapPin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import GlowCard from '../components/GlowCard';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';

const Contact = () => {
  const { toast } = useToast();
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);

  useEffect(() => {
    const fetchContactSettings = async () => {
      try {
        const response = await api.get('/settings/contact');
        setContactSettings(response.data);
      } catch (error) {
        // Use default values if fetch fails
        setContactSettings({
          email: 'hello@onlzcode.com',
          phone: '+1 (555) 123-4567',
          location: 'San Francisco, CA',
          business_hours: {
            weekdays: 'Monday - Friday: 9:00 AM - 6:00 PM',
            saturday: 'Saturday: 10:00 AM - 4:00 PM',
            sunday: 'Sunday: Closed'
          }
        });
      }
    };
    fetchContactSettings();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { contactAPI } = await import('../services/api');
      await contactAPI.submit(formData);
      
      toast({
        title: "Message sent!",
        description: "We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = contactSettings ? [
    { icon: Mail, label: 'Email', value: contactSettings.email },
    { icon: Phone, label: 'Phone', value: contactSettings.phone },
    { icon: MapPin, label: 'Location', value: contactSettings.location }
  ] : [];

  const glowColors = isDarkMode 
    ? ['rgba(138, 100, 255, 0.6)', 'rgba(100, 200, 255, 0.6)', 'rgba(255, 150, 100, 0.6)']
    : ['rgba(99, 102, 241, 0.3)', 'rgba(56, 189, 248, 0.3)', 'rgba(251, 146, 60, 0.3)'];

  return (
    <div className={`relative min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <MouseTrail />
      <Navigation />
      
      <main className="relative z-10 pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-8">
          <div className="mb-16">
            <h1 className={`text-6xl md:text-8xl font-light leading-tight mb-6 tracking-tight ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <TextScramble as="span" speed={30}>Get in Touch</TextScramble>
            </h1>
            <p className={`text-xl font-light ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <TextScramble as="span" speed={20}>Let's discuss your next project</TextScramble>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className={`block text-sm mb-2 uppercase tracking-wider ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`w-full ${
                      isDarkMode 
                        ? 'bg-transparent border-white/10 focus:border-white text-white' 
                        : 'bg-white border-gray-200 focus:border-gray-500 text-gray-900'
                    }`}
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className={`block text-sm mb-2 uppercase tracking-wider ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full ${
                      isDarkMode 
                        ? 'bg-transparent border-white/10 focus:border-white text-white' 
                        : 'bg-white border-gray-200 focus:border-gray-500 text-gray-900'
                    }`}
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className={`block text-sm mb-2 uppercase tracking-wider ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className={`w-full resize-none ${
                      isDarkMode 
                        ? 'bg-transparent border-white/10 focus:border-white text-white' 
                        : 'bg-white border-gray-200 focus:border-gray-500 text-gray-900'
                    }`}
                    placeholder="Tell us about your project..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={`group px-8 py-6 text-base transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-white text-black hover:bg-gray-200' 
                      : 'bg-gray-900 text-white hover:bg-gray-700'
                  }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </form>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className={`text-2xl font-light mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <TextScramble as="span" speed={30}>Contact Information</TextScramble>
                </h2>
                <div className="space-y-4">
                  {contactInfo.map((info, index) => {
                    const Icon = info.icon;
                    return (
                      <GlowCard 
                        key={index}
                        className={`border transition-all duration-500 group ${
                          isDarkMode 
                            ? 'border-white/10 bg-black/30' 
                            : 'border-gray-200 bg-white/80 shadow-md'
                        }`}
                        glowColor={glowColors[index % glowColors.length]}
                        glowSize={150}
                      >
                        <div className="p-4 flex items-start space-x-4">
                          <div className={`transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400 group-hover:text-white' : 'text-gray-500 group-hover:text-gray-900'
                          }`}>
                            <Icon size={20} />
                          </div>
                          <div>
                            <div className={`text-xs uppercase tracking-wider mb-1 ${
                              isDarkMode ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                              {info.label}
                            </div>
                            <div className={`transition-colors duration-300 ${
                              isDarkMode ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'
                            }`}>
                              {info.value}
                            </div>
                          </div>
                        </div>
                      </GlowCard>
                    );
                  })}
                </div>
              </div>

              <GlowCard 
                className={`border ${
                  isDarkMode 
                    ? 'border-white/10 bg-black/30' 
                    : 'border-gray-200 bg-white/80 shadow-md'
                }`}
                glowColor={isDarkMode ? "rgba(100, 255, 180, 0.5)" : "rgba(34, 197, 94, 0.3)"}
                glowSize={180}
              >
                <div className="p-6">
                  <h3 className={`text-sm uppercase tracking-wider mb-4 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>Business Hours</h3>
                  <div className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div>{contactSettings?.business_hours?.weekdays || 'Monday - Friday: 9:00 AM - 6:00 PM'}</div>
                    <div>{contactSettings?.business_hours?.saturday || 'Saturday: 10:00 AM - 4:00 PM'}</div>
                    <div>{contactSettings?.business_hours?.sunday || 'Sunday: Closed'}</div>
                  </div>
                </div>
              </GlowCard>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
